import { useEffect, useState } from 'react';
import { Slider } from '@mui/material';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import Input from '~/components/atoms/input/input';
import validateNumberRangeFilterStateChange from '~/components/organisms/filters/numberRangeFilter/validateNumberRangeFilterStateChange';
import commonFilterStyle from '~/components/styles/commonFilter.module.scss';
import helperStyle from '~/components/styles/helpers.module.scss';
import { NumberFilter_ControlsState, NumberFilter_State } from '~/core/features/filtering/types/numberFilter.type';
import { UpdateFilterControls } from '~/core/useSearchState';
import { numberFormatter } from '~/utils/numberFormatter';
import { getSliderStyles } from './getSliderStyles';
import { SliderHistogram } from './SliderHistogram';
import styles from './sliderRangeFilter.module.scss';

type SliderRangeFilterProps = {
    filterState: NumberFilter_State;
    filterControls: NumberFilter_ControlsState;
    updateFilterControls: UpdateFilterControls;
};

export const SliderRangeFilter = ({ filterState, filterControls, updateFilterControls }: SliderRangeFilterProps) => {
    const usingHistogram = filterState.configuration.subtype === 'histogram';

    // lower and upper bound specify the maximum / minimum possible value, that filter values can be set to - they
    // define the total range of the filter. These values are set based on applied aggregations in NumberFilter.ts
    const [lowerBound, setLowerBound] = useState(filterState.values.min.value);
    const [upperBound, setUpperBound] = useState(filterState.values.max.value);
    const histogramData = filterState.values.histogram;
    const [filterValues, setFilterValues] = useState([filterControls.values.min, filterControls.values.max]);

    // converts slider values into percentages relative to the defined range, facilitating visualization by mapping them within a standardized scale
    const calculateRelativeFilterValues = (values: number[], min: number, max: number): [number, number] => {
        const targetRange = upperBound - lowerBound;

        const percentages: [number, number] = [
            ((values[0] - lowerBound) / targetRange) * 100,
            ((values[1] - lowerBound) / targetRange) * 100,
        ];

        return percentages;
    };

    const sanitizeNumberInput = (value: string) => {
        return parseFloat(value.replace(/\D/g, ''));
    };

    useEffect(() => {
        setUpperBound(filterState.values.max.value);
        setLowerBound(filterState.values.min.value);
    }, [filterState.values.max.value, filterState.values.min.value]);

    useEffect(() => {
        setFilterValues([filterState.values.min.displayValue, filterState.values.max.displayValue]);
    }, [filterState]);

    const callUpdate = (changedValue: 'min' | 'max' | 'both') => {
        if (
            (changedValue === 'min' && filterValues[0] === filterState.values.min.displayValue) ||
            (changedValue === 'max' && filterValues[1] === filterState.values.max.displayValue)
        ) {
            return;
        }
        const { newMax, newMin } = validateNumberRangeFilterStateChange(
            filterValues[0],
            filterValues[1],
            lowerBound,
            upperBound,
            changedValue
        );

        updateFilterControls({
            value: {
                min: newMin,
                max: newMax,
            },
        });
    };

    const handleSliderChange = (event: Event, newValue: number | number[]) => {
        if (Array.isArray(newValue)) {
            setFilterValues(newValue);
        }
    };
    const handleSliderCommit = () => {
        callUpdate('both');
    };

    return (
        <div
            className={classNames(commonFilterStyle.serpFormCell, styles.wrapper)}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    callUpdate('both');
                }
            }}
            role="presentation"
            data-testid="filters-range-filter"
        >
            <div className={styles.inputs}>
                <div className={styles.inputWrapper}>
                    <label htmlFor="from" className={helperStyle.visuallyHidden}>
                        <FormattedMessage id="slider_from" defaultMessage="Od" />
                    </label>
                    <Input
                        type="text"
                        id={filterState.identifier + '-min'}
                        value={isNaN(filterValues[0]) ? '' : numberFormatter(filterValues[0])}
                        onChange={(e) => {
                            const sanitizedValue = sanitizeNumberInput(e.currentTarget.value);
                            setFilterValues([sanitizedValue, filterValues[1]]);
                        }}
                        onBlur={() => callUpdate('min')}
                        data-testid="filters-range-filter-min-input"
                        disabled={filterState.disabled}
                        enterKeyHint="done"
                    />
                </div>
                <div className={styles.inputSeparator} />
                <div className={styles.inputWrapper}>
                    <label htmlFor="to" className={helperStyle.visuallyHidden}>
                        <FormattedMessage id="slider_to" defaultMessage="Do" />
                    </label>
                    <Input
                        type="text"
                        id={filterState.identifier + '-max'}
                        value={isNaN(filterValues[1]) ? '' : numberFormatter(filterValues[1])}
                        onChange={(e) => {
                            const sanitizedValue = sanitizeNumberInput(e.currentTarget.value);
                            setFilterValues([filterValues[0], sanitizedValue]);
                        }}
                        onBlur={() => callUpdate('max')}
                        data-testid="filters-range-filter-max-input"
                        disabled={filterState.disabled}
                        enterKeyHint="done"
                    />
                </div>
                <span>{filterState.configuration.unit}</span>
            </div>
            <div className={styles.slider}>
                {histogramData && histogramData.length > 1 && !filterState.disabled && usingHistogram && (
                    <SliderHistogram
                        relativeSliderValues={calculateRelativeFilterValues(filterValues, lowerBound, upperBound)}
                        histogramData={histogramData}
                    />
                )}
                {filterState.disabled || (usingHistogram && (!histogramData || histogramData.length < 2)) ? (
                    <Slider min={0} max={100} sx={getSliderStyles()} value={[0, 100]} disabled />
                ) : (
                    <Slider
                        getAriaLabel={() => 'Price'}
                        value={filterValues}
                        min={lowerBound}
                        max={upperBound}
                        disableSwap
                        onChange={handleSliderChange}
                        onChangeCommitted={handleSliderCommit}
                        sx={getSliderStyles()}
                        data-testid="filters-range-filter-slider"
                    />
                )}
            </div>
        </div>
    );
};
